import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Mic, VolumeX } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed heights for voice bars
const LISTENING_BARS = [35, 50, 25, 55, 40];
const SPEAKING_BARS = [30, 45, 55, 35, 60, 40, 50];

export default function VoiceMode({ isOpen, onClose, token }) {
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [shouldStartListening, setShouldStartListening] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  const streamRef = useRef(null);
  const conversationHistoryRef = useRef([]);

  // Keep ref in sync with state
  useEffect(() => {
    conversationHistoryRef.current = conversationHistory;
  }, [conversationHistory]);

  const stopEverything = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsListening(false);
    setIsSpeaking(false);
    setIsProcessing(false);
    setShouldStartListening(false);
  }, []);

  const startListening = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        if (audioBlob.size > 1000) {
          // Process the voice input
          setIsProcessing(true);
          setTranscript("Procesando...");
          
          try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');
            
            const sttResponse = await axios.post(`${API}/voice/speech-to-text`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (!sttResponse.data.success || !sttResponse.data.text) {
              setTranscript("No te escuché bien...");
              setIsProcessing(false);
              setShouldStartListening(true);
              return;
            }
            
            const userText = sttResponse.data.text;
            setTranscript(userText);
            
            const currentHistory = conversationHistoryRef.current;
            const updatedHistory = [...currentHistory, { tipo: "user", texto: userText }];
            setConversationHistory(updatedHistory);
            
            const aiRes = await axios.post(`${API}/asistente-ia`, {
              pregunta: userText,
              empresa_id: null,
              historial: updatedHistory.slice(-10)
            }, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const responseText = aiRes.data.respuesta;
            setAiResponse(responseText);
            setConversationHistory([...updatedHistory, { tipo: "asistente", texto: responseText }]);
            
            setIsProcessing(false);
            
            // Speak the response - OPTIMIZADO PARA BAJA LATENCIA
            setIsSpeaking(true);
            // Limitar a 400 chars para respuesta más rápida
            const maxLength = 400;
            let textToSpeak = responseText
              .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
              .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
              .replace(/\*\*/g, '').replace(/\*/g, '')
              .replace(/\n+/g, '. ').trim();
            
            if (textToSpeak.length > maxLength) {
              const cutPoint = textToSpeak.lastIndexOf('.', maxLength);
              textToSpeak = cutPoint > 250 ? textToSpeak.substring(0, cutPoint + 1) : textToSpeak.substring(0, maxLength) + ".";
            }
            
            try {
              const ttsResponse = await axios.post(`${API}/voice/text-to-speech`, {
                text: textToSpeak,
                voice: "Laura",
                speed: 1.0
              }, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              
              if (ttsResponse.data.success && ttsResponse.data.audio_base64) {
                const audioSrc = `data:audio/mp3;base64,${ttsResponse.data.audio_base64}`;
                const audio = new Audio(audioSrc);
                currentAudioRef.current = audio;
                
                audio.onended = () => {
                  setIsSpeaking(false);
                  currentAudioRef.current = null;
                  setShouldStartListening(true);
                };
                
                audio.onerror = () => {
                  setIsSpeaking(false);
                  currentAudioRef.current = null;
                  setShouldStartListening(true);
                };
                
                // Reproducir inmediatamente
                await audio.play();
              } else {
                setIsSpeaking(false);
                setShouldStartListening(true);
              }
            } catch (ttsError) {
              console.error("TTS error:", ttsError);
              setIsSpeaking(false);
              setShouldStartListening(true);
            }
            
          } catch (error) {
            console.error("Error processing voice:", error);
            setTranscript("Error procesando...");
            setIsProcessing(false);
            setShouldStartListening(true);
          }
        } else {
          setShouldStartListening(true);
        }
      };
      
      mediaRecorder.start();
      setIsListening(true);
      setTranscript("");
      
    } catch (error) {
      console.error("Error accessing microphone:", error);
      toast.error("No se pudo acceder al micrófono");
    }
  }, [token]);

  // Effect to restart listening when needed
  useEffect(() => {
    if (shouldStartListening && !isListening && !isSpeaking && !isProcessing && isOpen) {
      setShouldStartListening(false);
      const timer = setTimeout(() => {
        startListening();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shouldStartListening, isListening, isSpeaking, isProcessing, isOpen, startListening]);

  // Auto-start listening when modal opens
  useEffect(() => {
    if (isOpen && !isListening && !isSpeaking && !isProcessing) {
      const timer = setTimeout(() => {
        startListening();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on close
  useEffect(() => {
    if (!isOpen) {
      stopEverything();
    }
  }, [isOpen, stopEverything]);

  const stopListening = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };

  const handleClose = () => {
    stopEverything();
    onClose();
  };

  const toggleMute = () => {
    if (isSpeaking && currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsSpeaking(false);
      setShouldStartListening(true);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          data-testid="close-voice-mode"
        >
          <X size={20} />
        </button>
        
        <div className="text-white/60 text-sm">
          Modo Voz
        </div>
        
        <button
          onClick={() => setShowSubtitles(!showSubtitles)}
          className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
            showSubtitles ? "bg-white/20 text-white" : "bg-white/10 text-white/50"
          }`}
          title="Mostrar/ocultar subtítulos"
        >
          <span className="text-xs font-bold">CC</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* Voice Visualization */}
        <div className="relative mb-8">
          {/* Outer glow */}
          <div className={`absolute inset-0 rounded-full transition-all duration-500 ${
            isListening 
              ? "bg-blue-500/30 blur-3xl scale-150 animate-pulse" 
              : isSpeaking 
                ? "bg-emerald-500/30 blur-3xl scale-150 animate-pulse"
                : isProcessing
                  ? "bg-amber-500/20 blur-2xl scale-125"
                  : "bg-white/10 blur-xl"
          }`} />
          
          {/* Main circle */}
          <div className={`relative w-48 h-48 rounded-full flex items-center justify-center transition-all duration-300 ${
            isListening 
              ? "bg-gradient-to-br from-blue-500 to-blue-700 shadow-2xl shadow-blue-500/50" 
              : isSpeaking 
                ? "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-2xl shadow-emerald-500/50"
                : isProcessing
                  ? "bg-gradient-to-br from-amber-500 to-amber-700 shadow-2xl shadow-amber-500/50"
                  : "bg-gradient-to-br from-slate-700 to-slate-800"
          }`}>
            {/* Inner visualization */}
            <div className="relative">
              {isListening && (
                <div className="flex items-center gap-1">
                  {LISTENING_BARS.map((height, i) => (
                    <div
                      key={i}
                      className="w-2 bg-white rounded-full animate-voice-bar"
                      style={{
                        height: `${height}px`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    />
                  ))}
                </div>
              )}
              
              {isSpeaking && (
                <div className="flex items-center gap-1.5">
                  {SPEAKING_BARS.map((height, i) => (
                    <div
                      key={i}
                      className="w-1.5 bg-white rounded-full animate-voice-bar"
                      style={{
                        height: `${height}px`,
                        animationDelay: `${i * 0.08}s`
                      }}
                    />
                  ))}
                </div>
              )}
              
              {isProcessing && (
                <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
              )}
              
              {!isListening && !isSpeaking && !isProcessing && (
                <Mic size={48} className="text-white/70" />
              )}
            </div>
          </div>
          
          {/* Ripple effect when listening */}
          {isListening && (
            <>
              <div className="absolute inset-0 rounded-full border-2 border-blue-400/50 animate-ping" />
              <div className="absolute inset-0 rounded-full border border-blue-300/30 animate-ping" style={{ animationDelay: '0.5s' }} />
            </>
          )}
        </div>

        {/* Status Text */}
        <div className="text-center mb-6">
          <p className={`text-lg font-medium transition-colors ${
            isListening ? "text-blue-400" :
            isSpeaking ? "text-emerald-400" :
            isProcessing ? "text-amber-400" :
            "text-white/50"
          }`}>
            {isListening ? "Escuchando..." :
             isSpeaking ? "Hablando..." :
             isProcessing ? "Pensando..." :
             "Toca para hablar"}
          </p>
        </div>

        {/* Subtitles */}
        {showSubtitles && (
          <div className="w-full max-w-lg text-center space-y-4">
            {transcript && (
              <div className="bg-white/5 rounded-2xl px-6 py-4 backdrop-blur-sm">
                <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Tú</p>
                <p className="text-white text-base">{transcript}</p>
              </div>
            )}
            
            {aiResponse && !isProcessing && (
              <div className="bg-emerald-500/10 rounded-2xl px-6 py-4 backdrop-blur-sm border border-emerald-500/20">
                <p className="text-emerald-400 text-xs uppercase tracking-wider mb-2">EDMUNDO</p>
                <p className="text-white text-base leading-relaxed">
                  {aiResponse.length > 300 ? aiResponse.substring(0, 300) + "..." : aiResponse}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Controls */}
      <div className="p-6 flex items-center justify-center gap-6">
        {/* Main mic button */}
        <button
          onClick={isListening ? stopListening : (isSpeaking ? toggleMute : startListening)}
          disabled={isProcessing}
          className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
            isListening 
              ? "bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/50" 
              : isSpeaking
                ? "bg-amber-500 hover:bg-amber-600"
                : "bg-white hover:bg-white/90"
          } disabled:opacity-50`}
          data-testid="voice-mode-mic"
        >
          {isListening ? (
            <div className="w-6 h-6 bg-white rounded-sm" />
          ) : isSpeaking ? (
            <VolumeX size={28} className="text-white" />
          ) : (
            <Mic size={28} className="text-slate-900" />
          )}
        </button>
      </div>

      {/* Helper text */}
      <div className="pb-8 text-center">
        <p className="text-white/40 text-sm">
          {isListening ? "Toca el botón rojo para enviar" :
           isSpeaking ? "Toca para interrumpir" :
           isProcessing ? "Procesando tu mensaje..." :
           "Toca el micrófono para empezar"}
        </p>
      </div>

      {/* CSS for voice bar animation */}
      <style>{`
        @keyframes voice-bar {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
        .animate-voice-bar {
          animation: voice-bar 0.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
